// Create-offeror-profile dialog. Opens from RLPDetail when a candidate is
// ready to package but no profile exists for the RLP. Pre-fills from the
// workspace settings so the common case is two clicks (review → Create).
//
// Scope (V1): the minimum fields form-filler needs to generate a non-empty
// GSA 527 draft. Deeper fields (officers, balance sheet, bank refs) live
// in the FormPackageDetail OfferorProfilePanel where the reviewer is
// already looking at the rendered form.

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useCreateOfferorProfile } from '@/lib/forms';
import { loadSettings } from '@/lib/settings';

interface Props {
  rlpId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (profileId: string) => void;
}

export function OfferorProfileDialog({ rlpId, open, onOpenChange, onCreated }: Props) {
  const settings = loadSettings().profile;

  const [legalEntity, setLegalEntity] = useState(settings.organization_name || settings.company);
  const [tradeStyle, setTradeStyle] = useState(settings.organization_tagline || '');
  const [pocName, setPocName] = useState(settings.full_name || '');
  const [pocEmail, setPocEmail] = useState(settings.email || '');
  const [pocPhone, setPocPhone] = useState(settings.phone || '');
  const [street, setStreet] = useState(settings.address_line1 || '');
  const [city, setCity] = useState(settings.city || '');
  const [stateVal, setStateVal] = useState(settings.state || '');
  const [zipCode, setZipCode] = useState(settings.zip || '');
  const [ein, setEin] = useState('');
  const [samUei, setSamUei] = useState('');
  const [samCage, setSamCage] = useState('');

  useEffect(() => {
    if (!open) return;
    const s = loadSettings().profile;
    setLegalEntity((prev) => prev || s.organization_name || s.company);
    setPocName((prev) => prev || s.full_name);
    setPocEmail((prev) => prev || s.email);
  }, [open]);

  const create = useCreateOfferorProfile(rlpId);

  const canSubmit = legalEntity.trim().length > 0 && !create.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      {
        legal_entity: legalEntity.trim(),
        trade_style_name: tradeStyle.trim() || null,
        poc_name: pocName.trim() || null,
        poc_email: pocEmail.trim() || null,
        poc_phone: pocPhone.trim() || null,
        street_address: street.trim() || null,
        city: city.trim() || null,
        state: stateVal.trim() || null,
        zip_code: zipCode.trim() || null,
        ein: ein.trim() || null,
        sam_uei: samUei.trim() || null,
        sam_cage: samCage.trim() || null,
      },
      {
        onSuccess: (res) => {
          onCreated?.(res.id);
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create offeror profile</DialogTitle>
          <DialogDescription>
            Identity + financials that appear on every GSA form. Pre-filled from your workspace
            settings — edit as needed. Additional detail (officers, balance sheet) can be added
            in the form review step.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label="Legal entity (required)" htmlFor="legal_entity">
            <Input
              id="legal_entity"
              value={legalEntity}
              onChange={(e) => setLegalEntity(e.target.value)}
              required
              autoFocus
            />
          </Field>

          <Field label="Trade style / DBA" htmlFor="trade_style">
            <Input
              id="trade_style"
              value={tradeStyle}
              onChange={(e) => setTradeStyle(e.target.value)}
              placeholder="Optional"
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="EIN" htmlFor="ein">
              <Input id="ein" value={ein} onChange={(e) => setEin(e.target.value)} placeholder="12-3456789" />
            </Field>
            <Field label="SAM UEI" htmlFor="sam_uei">
              <Input id="sam_uei" value={samUei} onChange={(e) => setSamUei(e.target.value)} />
            </Field>
            <Field label="CAGE code" htmlFor="sam_cage">
              <Input id="sam_cage" value={samCage} onChange={(e) => setSamCage(e.target.value)} />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="POC name" htmlFor="poc_name">
              <Input id="poc_name" value={pocName} onChange={(e) => setPocName(e.target.value)} />
            </Field>
            <Field label="POC email" htmlFor="poc_email">
              <Input
                id="poc_email"
                type="email"
                value={pocEmail}
                onChange={(e) => setPocEmail(e.target.value)}
              />
            </Field>
          </div>

          <Field label="POC phone" htmlFor="poc_phone">
            <Input
              id="poc_phone"
              value={pocPhone}
              onChange={(e) => setPocPhone(e.target.value)}
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr_1fr_1fr]">
            <Field label="Street" htmlFor="street">
              <Input id="street" value={street} onChange={(e) => setStreet(e.target.value)} />
            </Field>
            <Field label="City" htmlFor="city">
              <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} />
            </Field>
            <Field label="State" htmlFor="state">
              <Input id="state" value={stateVal} onChange={(e) => setStateVal(e.target.value)} />
            </Field>
            <Field label="ZIP" htmlFor="zip">
              <Input id="zip" value={zipCode} onChange={(e) => setZipCode(e.target.value)} />
            </Field>
          </div>

          {create.isError && (
            <p className="text-[0.8125rem] text-danger-bold">
              {create.error instanceof Error ? create.error.message : 'Failed to create profile'}
            </p>
          )}

          <div className="mt-2 flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending ? 'Creating…' : 'Create profile'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="flex flex-col gap-1 text-[0.8125rem]">
      <span className="font-semibold text-ink">{label}</span>
      {children}
    </label>
  );
}
